import Phaser from "phaser";
import type { TabViewContract, TabContentRect } from "@yjsgames/phaser-ui/tab-registry";
import type { Rect } from "@yjsgames/phaser-ui/panel-content";
import type { BigNumber } from "@yjsgames/bignumber";
import { NineSliceButton, NINE_SLICE_BUTTON_EVENT, ScrollableList } from "@yjsgames/phaser-ui";
import { FONT_FAMILY, PRIMARY_BUTTON_STYLE, setButtonEnabled, TYPO } from "./uiTokens.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import type { TextDirection } from "@yjsgames/i18n";
import { formatBig } from "./formatNumber.js";
import { computeStatUpgradeLayout, BUTTON_WIDTH } from "./statUpgradeLayout.js";
import { MultiplierSelector } from "./MultiplierSelector.js";
import type { Multiplier } from "../systems/multiBuy.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { TipCarouselView } from "./TipCarouselView.js";

/** 스탯 1행의 표시 상태 — 문구 조립(소스 합성·분해)은 tabData가 t()/formatBig으로 완성해 전달. */
export interface StatUpgradeRowState {
  readonly level: number;
  /** 1줄 제목+레벨 ("공격력 Lv.N"). */
  readonly cellName: string;
  /** 2줄 소스 분해 ("스탯+3 · 환생+5 · 유물×1.3"). */
  readonly breakdown: string;
  /** 3줄 현재 최종값 → 다음 예상값. */
  readonly result: string;
  /** 표시 비용 (현재 배수의 총비용; count==0이면 다음 1레벨 비용, 표시 시 formatBig). maxed면 무시. */
  readonly cost: BigNumber;
  /** 현재 배수로 구매할 레벨 수 (x10=10, xMax는 보유 골드 가변, 부족 시 0). 1보다 크면 비용에 ×count 표기. */
  readonly count: number;
  readonly affordable: boolean;
  readonly maxed: boolean;
}

export interface StatUpgradeData {
  /** statId → 행 상태. (보유 골드는 상단 HUD가 표시 — 본 탭에서는 제거.) */
  readonly rows: Record<string, StatUpgradeRowState>;
}

export interface StatUpgradeTabViewOptions {
  readonly onUpgrade: (statId: string) => void;
  /** 배수(x1/x10/x100/xMax) 변경 — 호출자가 각 행 비용/구매 가능 갱신(refresh). */
  readonly onMultiplierChange: (mult: Multiplier) => void;
}

/** 표시 순서 = statUpgrades.tsv statId 순서. 행 문구는 tabData가 statCell.* i18n 키로 완성. */
const STAT_IDS = ["attack", "fireRate", "goldGain", "critChance", "critDamage"] as const;
type StatId = (typeof STAT_IDS)[number];

const STAT_ICON_KEY: Record<StatId, string> = {
  attack: "icon_stat_attack",
  fireRate: "icon_stat_fire_rate",
  goldGain: "icon_stat_gold_gain",
  critChance: "icon_stat_crit_chance",
  critDamage: "icon_stat_crit_damage",
};

function buyButtonLabel(state?: StatUpgradeRowState): string {
  if (!state) return t("statUpgrade.buy");
  if (state.maxed) return t("statUpgrade.max");
  return state.count > 1 ? `${formatBig(state.cost)} x${state.count}` : formatBig(state.cost);
}

interface RowObjects {
  readonly statId: StatId;
  readonly frame: Phaser.GameObjects.Rectangle;
  readonly icon: Phaser.GameObjects.Image;
  /** 제목+레벨 (1줄). */
  readonly name: Phaser.GameObjects.Text;
  /** 소스 분해 (2줄). */
  readonly breakdown: Phaser.GameObjects.Text;
  /** 현재→다음 결과 (3줄). */
  readonly result: Phaser.GameObjects.Text;
  readonly button: NineSliceButton;
}

/**
 * 스탯 업그레이드 탭 — 골드 소모 영구 강화 5종 + 상단 고정 배수 선택 라디오(x1/x10/x100/xMax).
 * 강화 항목은 ScrollableList(phaser-ui)로 스크롤되어 패널 하단 밖으로 삐져나오지 않는다(헤더 title/배수는 고정).
 * 각 행은 컴팩트 3줄(제목+레벨 / 소스 분해(스탯·환생·유물) / 현재 최종값 → 다음 예상값) + 우측 강화 버튼·비용.
 * 합성·분해 문구는 tabData(buildStatUpgradeData)가 statComposition + t()로 완성하고, 뷰는 setText만 한다.
 *
 * 행은 생성자에서 1회 생성해 scrollList에 addContent하고 update()는 텍스트/버튼 상태만 갱신한다
 * (RebirthTabView와 동일 양식). 드래그 없음 → freezeItem 불필요.
 * 배치: 세로 스택(title/multiplier/scroll fill)·행 간격은 statUpgrade.json, 좌우/RTL/줄바꿈폭은 computeStatUpgradeLayout.
 */
export class StatUpgradeTabView implements TabViewContract<StatUpgradeData> {
  /** 팁 캐러셀 (재사용 컴포넌트, tab="statUpgrade") — 상단 "스탯 업그레이드" 타이틀은 제거됨. */
  private readonly tipView: TipCarouselView;
  private readonly multSelector: MultiplierSelector;
  private readonly scrollList: ScrollableList;
  private readonly rows: RowObjects[] = [];
  /** relayout(방향 전환)에서 computeStatUpgradeLayout 재호출용 콘텐츠 영역. */
  private readonly rect: { x: number; y: number; width: number };

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    private readonly panelBottom: number,
    opts: StatUpgradeTabViewOptions,
  ) {
    this.rect = { x, y, width };
    const dir = getTextDirection();
    const L = computeStatUpgradeLayout(
      { x, y, width, height: 0 },
      dir,
      STAT_IDS.length,
      this.panelBottom,
    );
    const rtox = L.rowTextOriginX;
    const wrapAlign = dir === "rtl" ? "right" : "left";

    // 팁 캐러셀(최상단) — 짧은 스탯 설명(2~3줄)을 랜덤 시작 + ◀/▶ 순환(재사용 컴포넌트, 전체폭 컨테이너).
    this.tipView = new TipCarouselView(scene, L.tip.x, L.tip.y, L.tip.width, L.tip.height, "statUpgrade");
    this.tipView.setDepth(2);
    scene.add.existing(this.tipView);

    // 제목 바로 아래 배수 선택 라디오 — 헤더 고정(스크롤 밖). 누르면 골드/레벨이 배수만큼 일괄 강화(RTL 셀 미러).
    this.multSelector = new MultiplierSelector(scene, L.multiplier.x, L.multiplier.y, {
      width,
      height: 26,
      dir,
      onChange: (mult) => opts.onMultiplierChange(mult),
    });

    // 스크롤 영역 — 배수 아래 ~ 패널 하단 inset.
    this.scrollList = new ScrollableList(scene, L.scroll.x, L.scroll.y, {
      width: L.scroll.width,
      height: L.scroll.height,
      barWidth: 5,
      barColor: PALETTE_NUM.scrollBar,
      trackColor: PALETTE_NUM.scrollTrack,
      trackAlpha: 0.65,
      barAlpha: 0.95,
      scrollSpeed: 36,
    });
    this.scrollList.setDepth(2);
    scene.add.existing(this.scrollList);

    // 스크롤 내부 행 — 로컬 좌표(computeStatUpgradeLayout rows). RTL이면 텍스트 우측, 버튼/비용 좌측.
    STAT_IDS.forEach((statId, i) => {
      const r = L.rows[i]!;
      const frame = scene.add
        .rectangle(0, 0, r.frame.width, r.frame.height, PALETTE_NUM.cellBg, 0.88)
        .setStrokeStyle(1, PALETTE_NUM.gridPathGlow, 0.28)
        .setDepth(1);
      const icon = scene.add
        .image(0, 0, STAT_ICON_KEY[statId])
        .setDisplaySize(20, 20)
        .setDepth(3);
      // 3줄 텍스트(제목/분해/결과) — wrapWidth로 우측 버튼 좌측 경계 침범 차단(큰 수에도 줄바꿈).
      const name = scene.add
        .text(0, 0, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.label, color: PALETTE.textPrimary })
        .setOrigin(rtox, 0.5)
        .setWordWrapWidth(r.wrapWidth)
        .setAlign(wrapAlign);
      const breakdown = scene.add
        .text(0, 0, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.caption, color: PALETTE.textDim })
        .setOrigin(rtox, 0.5)
        .setWordWrapWidth(r.wrapWidth)
        .setAlign(wrapAlign);
      const result = scene.add
        .text(0, 0, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.caption, color: PALETTE.textSecondary })
        .setOrigin(rtox, 0.5)
        .setWordWrapWidth(r.wrapWidth)
        .setAlign(wrapAlign);
      const button = new NineSliceButton(scene, 0, 0, {
        style: PRIMARY_BUTTON_STYLE,
        label: buyButtonLabel(),
        widthOverride: BUTTON_WIDTH,
        heightOverride: 34,
        iconKey: "icon_gold",
        iconSize: 12,
      });
      button.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onUpgrade(statId));

      this.scrollList.addContent(frame, r.frame.x, r.frame.y);
      this.scrollList.addContent(icon, r.icon.x, r.icon.y);
      this.scrollList.addContent(name, r.name.x, r.name.y);
      this.scrollList.addContent(breakdown, r.breakdown.x, r.breakdown.y);
      this.scrollList.addContent(result, r.result.x, r.result.y);
      this.scrollList.addContent(button, r.button.x, r.button.y);
      this.rows.push({ statId, frame, icon, name, breakdown, result, button });
    });
    this.scrollList.setContentHeight(L.contentHeight);
  }

  update(d: StatUpgradeData): void {
    for (const row of this.rows) {
      const state = d.rows[row.statId];
      if (!state) continue;
      row.name.setText(state.cellName);
      row.breakdown.setText(state.breakdown);
      row.result.setText(state.result);
      // 배수>1이면 총비용에 ×count 표기, 만렙은 MAX, 그 외(x1·xMax 부족)는 단일 비용 표기.
      row.button.setLabel(buyButtonLabel(state));
      row.button.setIconVisible(!state.maxed);
      setButtonEnabled(row.button, state.affordable && !state.maxed);
    }
  }

  /** 현재 선택 배수 — GameScene이 upgradeStat/buildStatUpgradeData에서 조회. */
  currentMultiplier(): Multiplier {
    return this.multSelector.current();
  }

  /** 가이드퀘스트 하이라이트용 — 현재 구매 가능한 첫 강화 버튼, 없으면 첫 버튼의 world rect. */
  upgradeButtonRect(): Rect | null {
    const row = this.rows.find((r) => r.button.isEnabled) ?? this.rows[0];
    if (!row) return null;
    const bounds = row.button.getBounds();
    const pad = 4;
    return {
      x: bounds.x - pad,
      y: bounds.y - pad,
      width: bounds.width + pad * 2,
      height: bounds.height + pad * 2,
    };
  }

  /** 언어 변경 — 정적 제목/배수 라벨/버튼만 갱신 (제목+레벨·분해·결과는 update()의 t() 보간으로). */
  applyLanguage(): void {
    this.tipView.applyLanguage();
    this.multSelector.applyLanguage();
  }

  /**
   * 방향 전환(RTL) — computeStatUpgradeLayout 재호출로 헤더(title/배수) + 스크롤 행 좌표·origin·wrapWidth 재배치.
   * 스크롤 행은 ScrollableList.relayoutContent로 lx/ly 교체(destroy 0). scrollList 자체 위치는 전체 폭이라 dir 무관.
   */
  relayout(dir: TextDirection): void {
    const L = computeStatUpgradeLayout(
      { ...this.rect, height: 0 },
      dir,
      STAT_IDS.length,
      this.panelBottom,
    );
    const wrapAlign = dir === "rtl" ? "right" : "left";
    this.tipView.setPosition(L.tip.x, L.tip.y);
    this.tipView.relayout(dir);
    this.multSelector.relayout(dir);
    const positions: { obj: Phaser.GameObjects.GameObject; lx: number; ly: number }[] = [];
    this.rows.forEach((row, i) => {
      const r = L.rows[i]!;
      row.name.setOrigin(L.rowTextOriginX, 0.5).setWordWrapWidth(r.wrapWidth).setAlign(wrapAlign);
      row.breakdown.setOrigin(L.rowTextOriginX, 0.5).setWordWrapWidth(r.wrapWidth).setAlign(wrapAlign);
      row.result.setOrigin(L.rowTextOriginX, 0.5).setWordWrapWidth(r.wrapWidth).setAlign(wrapAlign);
      positions.push(
        { obj: row.frame, lx: r.frame.x, ly: r.frame.y },
        { obj: row.icon, lx: r.icon.x, ly: r.icon.y },
        { obj: row.name, lx: r.name.x, ly: r.name.y },
        { obj: row.breakdown, lx: r.breakdown.x, ly: r.breakdown.y },
        { obj: row.result, lx: r.result.x, ly: r.result.y },
        { obj: row.button, lx: r.button.x, ly: r.button.y },
      );
    });
    this.scrollList.relayoutContent(positions);
  }

  /** 셸 펼치기/접기 — 콘텐츠 rect(상단) 갱신 + 스크롤 뷰포트 확장 후 relayout으로 제목/행 재배치(항목 더 보임). */
  resize(rect: TabContentRect): void {
    this.rect.x = rect.x;
    this.rect.y = rect.y;
    this.rect.width = rect.width;
    const dir = getTextDirection();
    const L = computeStatUpgradeLayout({ ...this.rect, height: 0 }, dir, STAT_IDS.length, this.panelBottom);
    this.scrollList.resize(L.scroll.x, L.scroll.y, L.scroll.width, L.scroll.height);
    this.relayout(dir);
  }

  /** 탭 표시/숨김 — 헤더(제목/배수) + 스크롤 리스트 전체를 일괄 토글(행은 scrollList 자식이라 함께 따라감). */
  setVisible(visible: boolean): void {
    this.tipView.setVisible(visible);
    this.multSelector.setVisible(visible);
    this.scrollList.setVisible(visible);
  }
}
